import React from 'react';
import { cn } from '@/lib/utils';
import { TextColors, TextSizes, TextWeights } from '@/types/types';

export interface TextProps {
  children: React.ReactNode;
  color?: TextColors;
  size?: TextSizes;
  weight?: TextWeights;
  onClick?: () => void;
  className?: string;
  testId?: string;
}

const colorMap: Record<TextColors, string> = {
  inherit: 'text-inherit',
  'base-blue-dark': 'text-blue-scale-900',
  'base-blue': 'text-foreground',
  blue: 'text-primary',
  red: 'text-destructive',
  disabled: 'text-muted-foreground',
  white: 'text-white',
  dark: 'text-blue-scale-900',
  black: 'text-black',
  'base-grey': 'text-muted-foreground',
  'new-grey': 'text-muted-foreground',
  'light-blue': 'text-primary/40',
  'placeholder-grey': 'text-muted-foreground',
  coral: 'text-brand-coral',
  teal: 'text-brand-teal',
  'final-grey-transparent': 'text-gray-500/20',
};

const sizeMap: Record<TextSizes, string> = {
  xxl: 'text-[32px] leading-[150%]',
  xl: 'text-2xl leading-[144%]',
  ll: 'text-xl leading-[132%]',
  l: 'text-lg leading-[132%]',
  m: 'text-base leading-[148%]',
  s: 'text-sm leading-[140%]',
  xs: 'text-xs leading-[140%]',
  xxs: 'text-[10px] leading-[140%]',
  xxxs: 'text-[6px] leading-[9px]',
};

const weightMap: Record<TextWeights, string> = {
  light: 'font-light',
  'light-medium': 'font-normal',
  medium: 'font-medium',
  'semi-bold': 'font-semibold',
  bold: 'font-bold',
  bolder: 'font-black',
};

export const Text: React.FC<TextProps> = ({
  children,
  color = 'inherit',
  size = 'm',
  weight = 'medium',
  onClick,
  className,
  testId,
}: TextProps) => {
  return (
    <span
      className={cn(
        colorMap[color],
        sizeMap[size],
        weightMap[weight],
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {children}
    </span>
  );
};
